// Labor Planning Calculator (guide 13.3).
export type LaborInputs = {
  weekly_sales?: number | null; // S
  target_splh?: number | null; // T, default 110
  scheduled_hours?: number | null; // H
  employees?: number | null; // E
  marks_formula?: number | null; // M
  avg_hours_per_employee?: number | null; // A, default 28
};

export type VarianceLabel = 'On Target' | 'Overscheduled' | 'Underscheduled';

export type LaborResults = {
  correct_hours: number | null; // C = S / T
  break_hours: number | null; // B = H / 5 * 0.5
  actual_hours: number | null; // Ha = H - B
  variance: number | null; // V = Ha - C
  variance_label: VarianceLabel | null;
  employees_needed: number | null; // round(C * E / H)
  employees_by_sales: number | null; // round(S / (T * A))
  mark_hours: number | null; // M + B
  mark_variance: number | null; // (M + B) - H
  mark_variance_label: VarianceLabel | null;
};

export const LABOR_DEFAULTS = { target_splh: 110, avg_hours_per_employee: 28 };

const round1 = (n: number) => Math.round(n * 10) / 10;
const num = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export function varianceLabel(v: number): VarianceLabel {
  if (Math.abs(v) <= 1) return 'On Target';
  return v > 0 ? 'Overscheduled' : 'Underscheduled';
}

export function computeLabor(input: LaborInputs): LaborResults {
  const S = num(input.weekly_sales);
  const T = num(input.target_splh) ?? LABOR_DEFAULTS.target_splh;
  const H = num(input.scheduled_hours);
  const E = num(input.employees);
  const M = num(input.marks_formula);
  const A = num(input.avg_hours_per_employee) ?? LABOR_DEFAULTS.avg_hours_per_employee;

  const empty: LaborResults = {
    correct_hours: null, break_hours: null, actual_hours: null, variance: null, variance_label: null,
    employees_needed: null, employees_by_sales: null, mark_hours: null, mark_variance: null, mark_variance_label: null,
  };
  // Results appear once S and T are entered.
  if (S == null || !T) return empty;

  const C = S / T;
  const out: LaborResults = { ...empty, correct_hours: round1(C) };
  if (A) out.employees_by_sales = Math.round(S / (T * A));
  // The break, variance and Mark rows need H.
  if (H != null && H > 0) {
    const B = (H / 5) * 0.5;
    const Ha = H - B;
    const V = Ha - C;
    out.break_hours = round1(B);
    out.actual_hours = round1(Ha);
    out.variance = round1(V);
    out.variance_label = varianceLabel(V);
    if (E != null) out.employees_needed = Math.round((C * E) / H);
    if (M != null) {
      out.mark_hours = round1(M + B);
      out.mark_variance = round1(M + B - H);
      out.mark_variance_label = varianceLabel(M + B - H);
    }
  }
  return out;
}
